/**
 * /practice — Coach's Plan hub (practice-hub-v3.md)
 *
 * Daily practice entry point. Surfaces a prescribed plan and launches the
 * session engine at /practice/session.
 */

import {
  type ReactNode,
  useState,
  useMemo,
  useReducer,
  useEffect,
  useCallback,
} from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  MVP_NODES,
  EXERCISE_SKILL_NODES,
  type CurriculumNode,
  type WellKnownStopKind,
} from "@music-app/learning-system";
import {
  usePrefsStore,
  useSavedPlansStore,
  loadActivitySessionsSince,
  endSession,
  type ActivitySessionRecord,
} from "@music-app/persistence";
import { usePracticeData } from "../lib/use-review-data.js";
import { hasLessonForNode } from "@music-app/lessons/runtime";
import {
  top3RecentWeakSpots,
  type CoachPlan,
  type PlanStop,
  type WeakSpot,
  type PlanStopKind,
} from "../lib/coach-plan.js";
import { PlanPicker } from "../components/PlanPicker.js";
import { PlanLibrary } from "../components/PlanLibrary.js";
import { WeakSpotCard } from "../components/WeakSpotCard.js";
import { PageHeader } from "../components/PageHeader.js";
import { buttonClasses } from "../components/Button.js";
import {
  summarizeSessions,
  currentStreak,
  todayMinutes,
} from "../lib/session-stats.js";
import { useRecentSessions, useActiveSession } from "../lib/use-recent-sessions.js";

// ─── constants ────────────────────────────────────────────────────────────────

const STOP_COLORS: Record<WellKnownStopKind, string> = {
  strengthen:  "text-warning",
  review:      "text-info",
  introduce:   "text-success",
  maintenance: "text-accent",
};

const STOP_DOT_BG: Record<WellKnownStopKind, string> = {
  strengthen:  "bg-warning",
  review:      "bg-info",
  introduce:   "bg-success",
  maintenance: "bg-accent",
};

const STOP_LEFT_RULE: Record<WellKnownStopKind, string> = {
  strengthen:  "border-l-4 border-l-warning",
  review:      "border-l-4 border-l-info",
  introduce:   "border-l-4 border-l-success",
  maintenance: "border-l-4 border-l-accent",
};

const STOP_LABELS: Record<WellKnownStopKind, string> = {
  strengthen:  "STRENGTHEN",
  review:      "REVIEW",
  introduce:   "INTRODUCE",
  maintenance: "KEEP SHARP",
};

// Safe lookup for unknown future stop kinds.
function stopAttr<T>(dict: Record<WellKnownStopKind, T>, kind: PlanStopKind, fallback: T): T {
  return (dict as Record<string, T>)[kind] ?? fallback;
}

const DURATION_OPTIONS = [10, 15, 20, 30] as const;

type SessionModifier = "skip-introduce" | "weak-spots-only" | null;

function isBypassQuizEligible(nodeId: string): boolean {
  return (
    !nodeId.startsWith("melodic-dictation") &&
    !nodeId.startsWith("rhythmic-dictation") &&
    !nodeId.startsWith("fret-")
  );
}

function nodeListenRoute(nodeId: string): string | null {
  for (const [skill, nodeIds] of Object.entries(EXERCISE_SKILL_NODES)) {
    if ((nodeIds as readonly string[]).includes(nodeId)) {
      const prefix = skill.startsWith("fret-") ? "/guitar" : "/exercise";
      return `${prefix}/${skill}?mode=listen&nodeId=${nodeId}`;
    }
  }
  return null;
}

// ─── page ─────────────────────────────────────────────────────────────────────

export function ReviewHubPage(): ReactNode {
  const navigate = useNavigate();

  const {
    cards,
    cardsLoaded,
    items,
    reviews,
    prefsLoaded,
    sessionTargetMinutes,
    setSessionTargetMinutes,
    onboardingComplete,
    placementCompleted,
    activePlan,
    generator,
    deps,
  } = usePracticeData();

  const setActiveSavedPlanId = usePrefsStore((s) => s.setActiveSavedPlanId);
  const setPlacementCompleted = usePrefsStore((s) => s.setPlacementCompleted);
  const userPlans = useSavedPlansStore((s) => s.plans);
  const [planLibraryOpen, setPlanLibraryOpen] = useState(false);

  // ── sessions ───────────────────────────────────────────────────────────────
  const { sessions, loaded: sessionsLoaded } = useRecentSessions(60);
  const { active: loadedActive } = useActiveSession();
  const [activeDiscarded, setActiveDiscarded] = useState(false);
  const active = activeDiscarded ? undefined : loadedActive;

  const handleDiscardActive = useCallback(() => {
    if (!loadedActive) return;
    void endSession(loadedActive.id, {
      itemsReviewed: loadedActive.itemsReviewed,
      correctCount: loadedActive.correctCount,
      averageResponseMs: loadedActive.averageResponseMs ?? 0,
    });
    setActiveDiscarded(true);
  }, [loadedActive]);

  const handleSkip = useCallback(() => {
    void setPlacementCompleted(true);
  }, [setPlacementCompleted]);

  // ── today's activity data ─────────────────────────────────────────────────
  const [todayActivitySessions, setTodayActivitySessions] = useState<ActivitySessionRecord[]>([]);
  useEffect(() => {
    const midnight = new Date();
    midnight.setHours(0, 0, 0, 0);
    loadActivitySessionsSince(midnight.toISOString())
      .then(setTodayActivitySessions)
      .catch(() => { /* non-fatal */ });
  }, []);

  // ── computed base data ─────────────────────────────────────────────────────
  const today = useMemo(() => new Date(), []);
  const todayMinutesSpent = useMemo(
    () => todayMinutes(sessions, todayActivitySessions, today),
    [sessions, todayActivitySessions, today],
  );
  const summary = useMemo(() => summarizeSessions(sessions), [sessions]);
  const streak = useMemo(() => currentStreak(sessions, today), [sessions, today]);

  // ── re-plan counter ────────────────────────────────────────────────────────
  const [replanTick, forceReplan] = useReducer((n: number) => n + 1, 0);

  // ── coach plan ─────────────────────────────────────────────────────────────
  const plan = useMemo<CoachPlan>(
    () => generator.build(deps, activePlan, sessionTargetMinutes),
    // replanTick forces a fresh Math.random() pick sequence
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [deps, activePlan, sessionTargetMinutes, generator, replanTick],
  );

  // ── session modifier (session-scoped only, resets on plan/duration change) ──
  const [sessionModifier, setSessionModifier] = useState<SessionModifier>(null);
  const toggleModifier = useCallback((modifier: NonNullable<SessionModifier>) => {
    setSessionModifier((prev) => (prev === modifier ? null : modifier));
  }, []);
  useEffect(() => { setSessionModifier(null); }, [activePlan, sessionTargetMinutes]);

  const effectiveStops = useMemo(() => {
    if (sessionModifier === "skip-introduce") return plan.stops.filter((s) => s.kind !== "introduce");
    if (sessionModifier === "weak-spots-only") return plan.stops.filter((s) => s.kind === "strengthen");
    return plan.stops;
  }, [plan.stops, sessionModifier]);

  // ── history check (gates the "Why this plan?" link) ───────────────────────
  const hasHistory = useMemo(
    () => Object.values(cards).some((c) => c.state !== "new"),
    [cards],
  );

  // ── weak spots ─────────────────────────────────────────────────────────────
  const weakSpots = useMemo(
    () => top3RecentWeakSpots(items, reviews, today),
    [items, reviews, today],
  );

  // ── next-due hints ─────────────────────────────────────────────────────────
  const nextDueHints = useMemo(() => {
    if (weakSpots.length === 0 || effectiveStops.length === 0) return [];
    const plannedNodeIds = new Set(effectiveStops.flatMap((s) => s.items.map((i) => i.nodeId)));
    return weakSpots
      .filter((spot) => !plannedNodeIds.has(spot.node.id))
      .flatMap((spot) => {
        const futureDates = items
          .filter((i) => i.nodeId === spot.node.id)
          .map((i) => cards[i.id]?.due)
          .filter((d): d is string => d !== undefined)
          .map((d) => new Date(d))
          .filter((d) => d > today);
        if (futureDates.length === 0) return [];
        const nextDue = new Date(Math.min(...futureDates.map((d) => d.getTime())));
        return [{ node: spot.node, nextDue, drillRoute: spot.drillRoute }];
      });
  }, [weakSpots, effectiveStops, items, cards, today]);

  // ── derived display state ──────────────────────────────────────────────────
  const noCardsYet = Object.keys(cards).length === 0;
  const isFirstTime = prefsLoaded && cardsLoaded && !placementCompleted && noCardsYet && !onboardingComplete;
  const hasResume = active && active.queue && active.queue.length > 0;
  const allCaughtUp = !isFirstTime && !hasResume && cardsLoaded && sessionsLoaded && plan.stops.length === 0;

  // ── adjusted total minutes (uses effectiveStops) ───────────────────────────
  const adjustedTotalMinutes = useMemo(
    () =>
      effectiveStops.reduce((sum, stop) => {
        if (stop.kind === "introduce") {
          const gatedNodes = stop.nodeAcquisition?.filter((a) => a.status === "needs-listen") ?? [];
          if (gatedNodes.length > 0) {
            const remaining = gatedNodes.reduce((s, a) => s + Math.max(0, a.threshold - a.exposures), 0);
            return sum + Math.max(1, Math.ceil(remaining / 6));
          }
        }
        return sum + stop.estimatedMinutes;
      }, 0),
    [effectiveStops],
  );

  const isOverBudget = effectiveStops.length > 0 && adjustedTotalMinutes > sessionTargetMinutes;

  // ── start session ──────────────────────────────────────────────────────────
  const gatedListenRoute = useCallback((stops: PlanStop[]): string | null => {
    const allItems = stops.flatMap((s) => s.items);
    const firstItem = allItems[0];
    if (!firstItem) return null;
    const introduceStop = stops.find((s) => s.kind === "introduce");
    if (!introduceStop) return null;
    const introduceIds = new Set(introduceStop.items.map((i) => i.id));
    if (!introduceIds.has(firstItem.id)) return null;
    const gatedNode = introduceStop.nodeAcquisition?.find((a) => a.status === "needs-listen");
    if (!gatedNode) return null;
    const route = nodeListenRoute(gatedNode.nodeId);
    if (!route) return null;
    const remaining = Math.max(1, gatedNode.threshold - gatedNode.exposures);
    return `${route}&coached=true&itemCount=${remaining}&from=today`;
  }, []);

  const handleStart = useCallback(() => {
    const listenRoute = gatedListenRoute(effectiveStops);
    if (listenRoute) { void navigate(listenRoute); return; }
    const allItems = effectiveStops.flatMap((s) => s.items);
    void navigate("/review/session", { state: { preSeededItems: allItems, planStops: effectiveStops } });
  }, [effectiveStops, navigate, gatedListenRoute]);

  const handleStartStop = useCallback((stop: PlanStop) => {
    const listenRoute = gatedListenRoute([stop]);
    if (listenRoute) { void navigate(listenRoute); return; }
    void navigate("/review/session", { state: { preSeededItems: stop.items, planStops: [stop] } });
  }, [navigate, gatedListenRoute]);

  const handleTestMeStop = useCallback((stop: PlanStop, nodeId: string) => {
    const items = stop.items.filter((i) => i.nodeId === nodeId);
    const filteredStop = { ...stop, items, nodeAcquisition: stop.nodeAcquisition?.filter((a) => a.nodeId === nodeId) };
    void navigate("/review/session", { state: { preSeededItems: items, planStops: [filteredStop], bypassQuiz: true } });
  }, [navigate]);

  // ── header text ────────────────────────────────────────────────────────────
  const stopCount = effectiveStops.length;
  const stopCountWord = stopCount === 1 ? "One stop" : stopCount === 2 ? "Two stops" : "Three stops";
  const planSubhead = stopCount > 0 ? `${stopCountWord}, ~${adjustedTotalMinutes} min.` : "Nothing due right now.";
  const weekday = today.toLocaleDateString("en-US", { weekday: "long" }).toLowerCase();
  const headerEyebrow = `${weekday}${streak > 0 ? ` · day ${streak} of your streak` : " · new week"}`;
  const headerSubhead = isFirstTime
    ? "Take a placement test to seed your schedule."
    : allCaughtUp
    ? `Nothing due in ${activePlan.name}.`
    : hasResume
    ? "You have an unfinished session."
    : planSubhead;

  // ── modifier visibility ─────────────────────────────────────────────────────
  const hasIntroduceStop = plan.stops.some((s) => s.kind === "introduce");
  const hasStrengthenStop = plan.stops.some((s) => s.kind === "strengthen");

  return (
    <div className="max-w-5xl mx-auto py-9 px-8">
      {planLibraryOpen && <PlanLibrary onClose={() => setPlanLibraryOpen(false)} />}
      <PageHeader
        eyebrow={headerEyebrow}
        title={noCardsYet ? "Welcome" : "Welcome back"}
        emphasis={noCardsYet ? undefined : "back"}
        subhead={headerSubhead}
        right={
          <div className="flex border border-ink/10">
            <StatTile
              label="Streak"
              value={streak === 0 ? "—" : `${streak}`}
              sublabel={streak === 1 ? "day" : streak > 0 ? "days" : undefined}
            />
            <StatTile
              label="Accuracy"
              value={summary.totalReviews > 0 ? `${Math.round(summary.accuracy * 100)}%` : "—"}
              bordered
              sublabel="14 days"
            />
          </div>
        }
      />

      {isFirstTime ? (
        <FirstTimeCard onSkip={handleSkip} />
      ) : hasResume ? (
        <ResumeCard active={active!} onDiscard={handleDiscardActive} />
      ) : (
        <section className="mb-8" aria-label="Coach's plan for today">

          {/* ── eyebrow row — always visible so the plan picker is reachable ── */}
          <div className="flex items-center justify-between mb-2">
            <PlanPicker
              activePlan={activePlan}
              mode={plan.mode}
              userPlans={userPlans}
              onSelect={(id) => void setActiveSavedPlanId(id)}
              onManagePlans={() => setPlanLibraryOpen(true)}
            />
            <div className="flex items-center gap-3">
              {hasHistory && (
                <Link
                  to="/practice/why"
                  className="font-mono text-[10px] text-inkFaded hover:text-ink transition-colors"
                >
                  Why these exercises? →
                </Link>
              )}
              {import.meta.env.DEV && (
                <Link
                  to="/review/explain"
                  className="font-mono text-[10px] text-inkFaded hover:text-ink transition-colors"
                  title="Dev: show plan trace"
                >
                  Explain plan →
                </Link>
              )}
              <button
                type="button"
                onClick={forceReplan}
                title="Picks a different random sample of items within each stop"
                className={buttonClasses("text")}
                aria-label="Re-shuffle items within stops"
              >
                ↻ Re-shuffle
              </button>
            </div>
          </div>

          {allCaughtUp ? (
            <AllCaughtUpCard planName={activePlan.name} />
          ) : (
            <>
              {/* ── daily goal reached ── */}
              {todayMinutesSpent >= sessionTargetMinutes && (
                <p className="font-mono text-[10px] text-success mb-3">Daily goal reached.</p>
              )}

              {/* ── intervention banner ── */}
              {plan.mode === "intervention" && plan.interventionNodeTitle && (
                <p className="font-sans text-xs text-warning bg-warning/10 px-3 py-2 mb-3">
                  Pausing new material — focusing on {plan.interventionNodeTitle} until recall improves.
                </p>
              )}

              {/* ── stop rows ── */}
              <div className="space-y-3 mb-4">
                {effectiveStops.map((stop) => (
                  <PlanStopRow
                    key={stop.kind}
                    stop={stop}
                    nodes={MVP_NODES}
                    onStartStop={handleStartStop}
                    onTestMe={handleTestMeStop}
                    isOnly={effectiveStops.length === 1}
                  />
                ))}
              </div>

              {/* ── next-due hints ── */}
              {nextDueHints.length > 0 && (
                <div className="mb-4 space-y-1" aria-label="Recently missed items returning soon">
                  {nextDueHints.map(({ node, nextDue, drillRoute }) => (
                    <p key={node.id} className="font-mono text-[10px] text-inkFaded">
                      {node.title} · missed items due back {relativeDate(nextDue, today)} ·{" "}
                      <Link
                        to={drillRoute.replace("mode=listen", "mode=practice") + "&from=today"}
                        className="underline hover:text-ink transition-colors"
                      >
                        drill now →
                      </Link>
                    </p>
                  ))}
                </div>
              )}

              {/* ── intent modifiers ── */}
              {(hasIntroduceStop || hasStrengthenStop) && (
                <IntentModifiers
                  modifier={sessionModifier}
                  onToggle={toggleModifier}
                  showSkipIntroduce={hasIntroduceStop}
                  showFocusWeak={hasStrengthenStop}
                />
              )}

              {/* ── duration picker ── */}
              <DurationPicker
                value={sessionTargetMinutes}
                onChange={(v) => void setSessionTargetMinutes(v)}
              />

              {/* ── start row ── */}
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  type="button"
                  onClick={handleStart}
                  disabled={effectiveStops.length === 0}
                  className="flex-1 bg-ink text-paper px-[18px] py-3 font-sans text-sm font-medium hover:bg-accentSoft transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  ▶ Start session · ~{adjustedTotalMinutes} min
                </button>
                <Link
                  to="/review/customize"
                  state={{ plan }}
                  className={buttonClasses("secondary") + " text-center"}
                >
                  Customize →
                </Link>
              </div>
              {isOverBudget && (
                <p className="font-mono text-[10px] text-inkFaded mt-1.5">
                  Queue longer than goal — consider{" "}
                  <button
                    type="button"
                    onClick={() => void setActiveSavedPlanId("app-review-sprint")}
                    className="underline hover:text-ink transition-colors"
                  >
                    Review Sprint
                  </button>
                  .
                </p>
              )}
            </>
          )}

        </section>
      )}

      {/* ── weak spots ── */}
      {weakSpots.length > 0 && (
        <WeakSpotsRow spots={weakSpots} elevated={allCaughtUp} />
      )}

      {/* ── footer ── */}
      {sessionsLoaded && summary.sessionCount > 0 && (
        <p className="font-mono text-[10px] text-inkFaded text-center mt-4 mb-8">
          Last {summary.sessionCount} sessions · {Math.round(summary.accuracy * 100)}% accuracy ·{" "}
          <Link to="/stats" className="underline hover:text-ink transition-colors">
            see all stats →
          </Link>
        </p>
      )}
    </div>
  );
}

// ─── IntentModifiers ──────────────────────────────────────────────────────────

function IntentModifiers({
  modifier,
  onToggle,
  showSkipIntroduce,
  showFocusWeak,
}: {
  modifier: SessionModifier;
  onToggle: (m: NonNullable<SessionModifier>) => void;
  showSkipIntroduce: boolean;
  showFocusWeak: boolean;
}): ReactNode {
  return (
    <div className="flex flex-wrap gap-4 mb-3" aria-label="Session modifiers">
      {showSkipIntroduce && (
        <button
          type="button"
          onClick={() => onToggle("skip-introduce")}
          className={`font-mono text-[10px] uppercase tracking-[0.18em] transition-colors ${
            modifier === "skip-introduce" ? "text-accent" : "text-inkFaded hover:text-inkSoft"
          }`}
          aria-pressed={modifier === "skip-introduce"}
        >
          {modifier === "skip-introduce" ? "✓ " : ""}Skip new material today
        </button>
      )}
      {showFocusWeak && (
        <button
          type="button"
          onClick={() => onToggle("weak-spots-only")}
          className={`font-mono text-[10px] uppercase tracking-[0.18em] transition-colors ${
            modifier === "weak-spots-only" ? "text-accent" : "text-inkFaded hover:text-inkSoft"
          }`}
          aria-pressed={modifier === "weak-spots-only"}
        >
          {modifier === "weak-spots-only" ? "✓ " : ""}Focus on weak spots
        </button>
      )}
    </div>
  );
}

// ─── DurationPicker ───────────────────────────────────────────────────────────

function DurationPicker({
  value,
  onChange,
}: {
  value: number;
  onChange: (v: number) => void;
}): ReactNode {
  return (
    <div className="flex items-center gap-2 mb-4" aria-label="Session duration">
      <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-inkFaded shrink-0">
        Duration
      </span>
      <div className="flex gap-1">
        {DURATION_OPTIONS.map((mins) => (
          <button
            key={mins}
            type="button"
            onClick={() => onChange(mins)}
            className={`font-mono text-[10px] px-2.5 py-1 transition-colors ${
              value === mins
                ? "bg-ink text-paper"
                : "border border-ink/20 text-inkSoft hover:border-ink/40"
            }`}
            aria-pressed={value === mins}
            aria-label={`${mins} minutes`}
          >
            {mins}
          </button>
        ))}
      </div>
      <span className="font-mono text-[10px] text-inkFaded">min</span>
    </div>
  );
}

// ─── PlanStopRow ──────────────────────────────────────────────────────────────

function PlanStopRow({
  stop,
  nodes,
  onStartStop,
  onTestMe,
  isOnly,
}: {
  stop: PlanStop;
  nodes: CurriculumNode[];
  onStartStop: (stop: PlanStop) => void;
  onTestMe?: (stop: PlanStop, nodeId: string) => void;
  isOnly?: boolean;
}): ReactNode {
  // REVIEW keeps expand/collapse; STRENGTHEN and INTRODUCE always show full content
  const isExpandable = stop.kind === "review";
  const [expanded, setExpanded] = useState(false);

  const nodeBreakdown = useMemo(() => {
    const groups = new Map<string, { title: string; count: number }>();
    for (const item of stop.items) {
      const existing = groups.get(item.nodeId);
      if (existing) {
        existing.count++;
      } else {
        const title = nodes.find((n) => n.id === item.nodeId)?.title ?? item.nodeId;
        groups.set(item.nodeId, { title, count: 1 });
      }
    }
    return Array.from(groups.entries()).map(([nodeId, v]) => ({ nodeId, ...v }));
  }, [stop.items, nodes]);

  // Header node text: condensed list for REVIEW, full names for STRENGTHEN/INTRODUCE
  const headerNodeText = useMemo(() => {
    const names = nodeBreakdown.map((n) => n.title);
    if (stop.kind !== "review" || names.length <= 3) return names.join(" · ");
    return names.slice(0, 3).join(" · ") + ` · +${names.length - 3} more`;
  }, [nodeBreakdown, stop.kind]);

  const gatedNodes = stop.kind === "introduce"
    ? (stop.nodeAcquisition?.filter((a) => a.status === "needs-listen") ?? [])
    : [];
  const listenRemaining = gatedNodes.reduce((s, a) => s + Math.max(0, a.threshold - a.exposures), 0);

  return (
    <div
      className={`bg-paperWarm border border-ink/10 ${stopAttr(STOP_LEFT_RULE, stop.kind, "border-l-4 border-l-inkFaded")}`}
      aria-label={stopAttr(STOP_LABELS, stop.kind, stop.kind.toUpperCase())}
    >
      {/* ── header ── */}
      <div className="flex items-start gap-3 px-4 pt-4 pb-2">
        <span
          className={`shrink-0 w-2 h-2 rounded-full mt-[5px] ${stopAttr(STOP_DOT_BG, stop.kind, "bg-inkFaded")}`}
          aria-hidden="true"
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-2 flex-wrap">
            <span className={`font-mono text-[10px] uppercase tracking-[0.18em] font-medium shrink-0 ${stopAttr(STOP_COLORS, stop.kind, "text-inkSoft")}`}>
              {stopAttr(STOP_LABELS, stop.kind, stop.kind.toUpperCase())}
            </span>
            {headerNodeText && (
              <span className="font-serif text-base text-ink leading-snug min-w-0 flex-1">
                {headerNodeText}
              </span>
            )}
            <span className="font-mono text-[10px] text-inkFaded tabular-nums shrink-0 ml-auto">
              {gatedNodes.length > 0
                ? `${listenRemaining} examples · ~${Math.max(1, Math.ceil(listenRemaining / 6))} min`
                : `${stop.itemCount} items · ~${stop.estimatedMinutes} min`}
            </span>
          </div>
        </div>
        {isExpandable && (
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            aria-expanded={expanded}
            aria-label={expanded ? "Collapse skills" : "Expand skills"}
            className={`shrink-0 font-mono text-inkFaded text-xs ml-1 transition-transform duration-150 ${expanded ? "rotate-90" : ""}`}
          >
            ›
          </button>
        )}
      </div>

      {/* ── reason + per-stop start ── */}
      <div className="flex items-baseline justify-between gap-3 px-4 pb-3 pl-9">
        <p className="font-serif italic text-sm text-inkSoft leading-snug">
          {stop.reason}
        </p>
        {!isOnly && (
          <button
            type="button"
            onClick={() => onStartStop(stop)}
            className={`${buttonClasses("text")} shrink-0`}
          >
            ▶ Start just this →
          </button>
        )}
      </div>

      {/* ── re-listen nudge (strengthen only) ── */}
      {stop.kind === "strengthen" && stop.suggestRelisten && stop.suggestRelistenRoute && (
        <div className="mx-4 mb-3 flex items-center justify-between gap-3 flex-wrap border border-warning/30 bg-warning/5 px-3 py-2">
          <p className="font-mono text-[9px] text-inkSoft">
            High error rate — re-listening may help more than drilling.
          </p>
          <Link
            to={stop.suggestRelistenRoute + "&from=today"}
            className="font-mono text-[9px] uppercase tracking-widest px-2 py-0.5 bg-warning text-paper hover:opacity-90 transition-opacity shrink-0"
          >
            Listen →
          </Link>
        </div>
      )}

      {/* ── INTRODUCE: acquisition details always visible ── */}
      {stop.kind === "introduce" && nodeBreakdown.length > 0 && (
        <div className="border-t border-ink/10" role="list" aria-label="Skills in this stop">
          {nodeBreakdown.map(({ nodeId, title }) => {
            const acq = stop.nodeAcquisition?.find((a) => a.nodeId === nodeId);
            const fillPct = acq ? Math.min(100, Math.round((acq.exposures / acq.threshold) * 100)) : 0;
            const baseListenRoute = nodeListenRoute(nodeId);
            const listenRoute = baseListenRoute && acq?.status === "needs-listen"
              ? `${baseListenRoute}&coached=true&itemCount=${Math.max(1, acq.threshold - acq.exposures)}&from=today`
              : baseListenRoute ? `${baseListenRoute}&from=today` : null;
            return (
              <div
                key={nodeId}
                className="flex items-start justify-between px-4 py-2.5 pl-9 border-b border-ink/10 last:border-b-0"
                role="listitem"
              >
                <div className="flex flex-col gap-1 min-w-0 flex-1 mr-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-serif text-sm text-ink leading-snug">{title}</span>
                    {listenRoute && (
                      <Link
                        to={listenRoute}
                        className="font-mono text-[9px] uppercase tracking-widest px-2 py-0.5 bg-info text-paper hover:opacity-90 transition-opacity shrink-0"
                      >
                        Listen →
                      </Link>
                    )}
                    {hasLessonForNode(nodeId) && (
                      <Link
                        to={`/learn/${nodeId}`}
                        className="font-mono text-[9px] uppercase tracking-widest text-inkFaded hover:text-accent transition-colors shrink-0"
                      >
                        Read →
                      </Link>
                    )}
                  </div>
                  {acq && acq.status === "needs-listen" && (
                    <>
                      <div className="flex items-center gap-2">
                        <div className="h-0.5 bg-ink/10 overflow-hidden flex-1">
                          <div className="h-full bg-success" style={{ width: `${fillPct}%` }} />
                        </div>
                        <span className="font-mono text-[9px] text-inkFaded tabular-nums shrink-0">
                          {acq.exposures}/{acq.threshold}
                        </span>
                      </div>
                      {isBypassQuizEligible(nodeId) && (
                        <button
                          type="button"
                          onClick={() => onTestMe?.(stop, nodeId)}
                          className="font-mono text-[9px] uppercase tracking-widest text-inkFaded hover:text-ink transition-colors text-left"
                        >
                          Test me →
                        </button>
                      )}
                    </>
                  )}
                  {acq && acq.status === "ready" && (
                    <p className="font-mono text-[9px] text-success">✓ Ready to practice</p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── REVIEW: expanded node breakdown ── */}
      {stop.kind === "review" && expanded && (
        <div className="border-t border-ink/10" role="list" aria-label="Skills in this stop">
          {nodeBreakdown.map(({ nodeId, title, count }) => (
            <div
              key={nodeId}
              className="flex items-center justify-between px-4 py-2.5 pl-9 border-b border-ink/10 last:border-b-0"
              role="listitem"
            >
              <span className="font-serif text-sm text-ink leading-snug">{title}</span>
              <span className="font-mono text-sm text-inkFaded tabular-nums shrink-0">{count}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── empty state cards ────────────────────────────────────────────────────────

function FirstTimeCard({ onSkip }: { onSkip: () => void }): ReactNode {
  return (
    <section className="mb-8" aria-label="Welcome — get started">
      <p className="font-mono text-[10px] uppercase tracking-widest text-accent mb-3">
        your coach recommends
      </p>
      <p className="font-serif text-xl font-medium text-ink mb-2">
        Take a 2-minute placement test.
      </p>
      <p className="font-serif italic text-base text-inkSoft mb-6 max-w-lg">
        It seeds the scheduler with what you already know — so it spends its time where you actually need it.
      </p>
      <div className="flex gap-3 flex-wrap">
        <Link to="/placement" className={buttonClasses("primary")}>Take the placement →</Link>
        <button type="button" onClick={onSkip} className={buttonClasses("secondary")}>
          Skip — start from scratch
        </button>
      </div>
    </section>
  );
}

function ResumeCard({
  active,
  onDiscard,
}: {
  active: { itemsReviewed: number; queue?: unknown[] };
  onDiscard: () => void;
}): ReactNode {
  return (
    <section className="mb-8" aria-label="Resume unfinished session">
      <p className="font-mono text-[10px] uppercase tracking-widest text-warning mb-3">
        unfinished session
      </p>
      <p className="font-serif text-xl font-medium text-ink mb-2">
        {active.itemsReviewed} reviewed · {active.queue?.length ?? 0} left
      </p>
      <p className="font-serif italic text-base text-inkSoft mb-6">
        Your session is still in progress. Resume where you left off.
      </p>
      <div className="flex gap-3 flex-wrap">
        <Link to="/review/session" className={buttonClasses("primary")}>Resume practice →</Link>
        <button type="button" onClick={onDiscard} className={buttonClasses("secondary")}>
          Discard and re-plan
        </button>
      </div>
    </section>
  );
}

function AllCaughtUpCard({ planName }: { planName: string }): ReactNode {
  return (
    <section className="mb-8" aria-label="All caught up">
      <p className="font-mono text-[10px] uppercase tracking-widest text-success mb-3">all caught up</p>
      <p className="font-serif text-xl font-medium text-ink mb-2">Nothing to practice yet in {planName}.</p>
      <p className="font-serif italic text-base text-inkSoft mb-6">
        Start by listening to a skill in the Ear Training hub to seed your first reviews.
      </p>
      <Link to="/exercise" className={buttonClasses("secondary")}>Browse drills →</Link>
    </section>
  );
}

// ─── weak spots ───────────────────────────────────────────────────────────────

function WeakSpotsRow({ spots, elevated }: { spots: WeakSpot[]; elevated?: boolean }): ReactNode {
  const earSpots = spots.filter((s) => s.node.track !== "guitar");
  const guitarSpots = spots.filter((s) => s.node.track === "guitar");
  const grouped = earSpots.length > 0 && guitarSpots.length > 0;

  return (
    <section className="mb-8" aria-label="Skills needing work">
      {elevated && (
        <p className="font-serif italic text-xl text-ink mb-2">Where to go next</p>
      )}
      <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-inkFaded mb-3">
        needs work · 7 days
      </p>
      {grouped ? (
        <div className="space-y-5">
          <div>
            <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-inkFaded mb-2">Ear Training</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {earSpots.map((spot) => <WeakSpotCard key={spot.node.id} spot={spot} />)}
            </div>
          </div>
          <div>
            <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-inkFaded mb-2">Guitar</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {guitarSpots.map((spot) => <WeakSpotCard key={spot.node.id} spot={spot} />)}
            </div>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {spots.map((spot) => <WeakSpotCard key={spot.node.id} spot={spot} />)}
        </div>
      )}
    </section>
  );
}

// ─── helpers ─────────────────────────────────────────────────────────────────

function relativeDate(due: Date, now: Date): string {
  const diffDays = Math.ceil((due.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
  if (diffDays <= 0) return "later today";
  if (diffDays === 1) return "tomorrow";
  return `in ${diffDays} days`;
}

// ─── header components ────────────────────────────────────────────────────────

function StatTile({
  label,
  value,
  sublabel,
  bordered,
}: {
  label: string;
  value: string;
  sublabel?: string;
  bordered?: boolean;
}): ReactNode {
  const isEmpty = value === "—";
  return (
    <div
      className={`p-4 ${bordered ? "border-l border-ink/10" : ""}`}
      aria-label={isEmpty ? `${label}: no data yet` : undefined}
    >
      <div className="font-mono text-[10px] uppercase tracking-widest text-inkFaded mb-1">
        {label}
      </div>
      <div className="font-serif text-xl font-medium text-ink tabular-nums" aria-hidden={isEmpty || undefined}>
        <span aria-hidden={isEmpty || undefined}>{value}</span>
        {sublabel && (
          <span className="text-xs text-inkSoft ml-1 font-mono not-italic">{sublabel}</span>
        )}
      </div>
    </div>
  );
}
